import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap, take, tap } from 'rxjs/operators';
import { AuthService } from '@fb-tool-front/core/services/auth.service';
import { Client, ClientInfo } from '@fb-tool-front/shared/interfaces/auth.type';
import { StripeService } from '@fb-tool-front/stripe/services/stripe.service';
import { Stripe } from '@fb-tool-front/stripe/models/stripe.model';
import { TuiNotification, TuiAlertService } from '@taiga-ui/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class StripeStore {
  /* Subscriptions */
  private readonly _subscriptions = new BehaviorSubject<Stripe.Subscription[]>([]);
  readonly subscriptions$ = this._subscriptions.asObservable();
  readonly isDemoMode$ = this.subscriptions$.pipe(
    map(
      (subscriptions: Stripe.Subscription[]) =>
        subscriptions.length === 0 || subscriptions[0].status !== 'active'
    )
  );

  get subscriptions(): Stripe.Subscription[] {
    return this._subscriptions.getValue();
  }

  set subscriptions(val: Stripe.Subscription[]) {
    this._subscriptions.next(val);
  }

  /* Subscriptions */

  /* Products */
  private readonly _products = new BehaviorSubject<Stripe.Product[]>([]);
  readonly products$ = this._products.asObservable();

  get products(): Stripe.Product[] {
    return this._products.getValue();
  }

  set products(val: Stripe.Product[]) {
    this._products.next(val);
  }

  /* Products */

  /* Current product */
  private readonly _currentProduct = new BehaviorSubject<Stripe.Product | string>(null);
  readonly currentProduct$ = this._currentProduct.asObservable();

  get currentProduct(): Stripe.Product | string {
    return this._currentProduct.getValue();
  }

  set currentProduct(val: Stripe.Product | string) {
    this._currentProduct.next(val);
  }

  /* Current product */

  isOverlayProductCountExceeded$ = forkJoin([
    this.currentProduct$.pipe(
      filter(currentProduct => !!currentProduct),
      take(1)
    ),
    this.authService.getClientInfo$(this.authService.getCurrentClient().id)
  ]).pipe(
    map(([currentProduct, clientInfo]: [Stripe.Product, ClientInfo]) => {
      return typeof currentProduct === 'string'
        ? false
        : Math.floor(
            ((clientInfo.overlay_product_count - parseInt(currentProduct.metadata.product_count)) /
              parseInt(currentProduct.metadata.product_count)) *
              100
          ) > 10;
    })
  );

  constructor(
    private stripeService: StripeService,
    private authService: AuthService,
    private notificationsService: TuiAlertService
  ) {
    combineLatest([this.getSubscriptions$(), this.getProducts$()]).subscribe(
      ([subscriptions, products]: [Stripe.Subscription[], Stripe.Product[]]) => {
        this.currentProduct =
          products.find(
            (product: Stripe.Product) => subscriptions?.[0]?.plan?.product === product.id
          ) ?? subscriptions?.[0]?.plan?.product;
      }
    );

    this.isOverlayProductCountExceeded$.pipe(filter(res => res)).subscribe(res =>
      this.notificationsService
        .open('Exceeded the number of products on the plan', {
          status: TuiNotification.Error
        })
        .pipe(untilDestroyed(this))
        .subscribe()
    );
  }

  getSubscriptions$(): Observable<Stripe.Subscription[]> {
    return this.authService.client$.pipe(
      filter((client: Client) => !!client),
      distinctUntilChanged((x: Client, y: Client) => x?.id === y?.id),
      switchMap((client: Client) =>
        this.stripeService.getSubscriptions({ client: client.id }).pipe(
          tap((subscriptions: Stripe.Subscription[]) => (this.subscriptions = subscriptions)),
          switchMap(() => this.subscriptions$)
        )
      )
    );
  }

  getProducts$(): Observable<Stripe.Product[]> {
    return this.stripeService.getProducts().pipe(
      map((products: Stripe.Product[]) =>
        products.sort(
          (a: Stripe.Product, b: Stripe.Product) =>
            parseInt(a.metadata.product_count) - parseInt(b.metadata.product_count)
        )
      ),
      tap((products: Stripe.Product[]) => (this.products = products)),
      switchMap(() => this.products$)
    );
  }
}
